import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import gamesData from '../data/games.json';

interface Game {
  id: number;
  name: string;
  description: string;
  thumbnail: string;
  url: string;
  category: string;
}

const GamePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [game, setGame] = useState<Game | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [relatedGames, setRelatedGames] = useState<Game[]>([]);
  
  useEffect(() => {
    // Simulate loading
    setIsLoading(true);
    
    // Find the game by ID
    const gameId = parseInt(id || '0', 10);
    const foundGame = gamesData.games.find(g => g.id === gameId);
    
    if (foundGame) {
      setGame(foundGame);
      
      // Find related games (same category)
      const related = gamesData.games
        .filter(g => g.category === foundGame.category && g.id !== gameId)
        .slice(0, 4);
      setRelatedGames(related);
    }
    
    // Simulating network delay
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
    
    // Scroll to top
    window.scrollTo(0, 0);
  }, [id]);
  
  if (isLoading) {
    return (
      <div className="container-custom py-20 text-center">
        <div className="animate-pulse flex flex-col items-center">
          <div className="h-8 w-40 bg-dark-light rounded mb-6"></div>
          <div className="h-64 w-full max-w-3xl bg-dark-light rounded"></div>
        </div>
      </div>
    );
  }
  
  if (!game) {
    return (
      <div className="container-custom py-20 text-center">
        <h2 className="text-2xl text-white mb-4">Game Not Found</h2>
        <p className="text-gray-400 mb-8">The game you're looking for doesn't exist or has been removed.</p>
        <button 
          onClick={() => navigate('/')}
          className="btn btn-primary"
        >
          Back to Home
        </button>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen">
      {/* Game Title */}
      <div className="bg-dark-light py-6">
        <div className="container-custom">
          <h1 className="text-3xl font-bold text-white">{game.name}</h1>
          <div className="flex flex-wrap gap-2 mt-2">
            <span className="bg-primary px-3 py-1 text-sm rounded-full text-white">
              {game.category}
            </span>
          </div>
        </div>
      </div>
      
      {/* Game content */}
      <div className="container-custom py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Game details & iframe */}
          <div className="lg:col-span-8">
            <div className="bg-dark-light rounded-lg overflow-hidden mb-8">
              <div className="aspect-video">
                <iframe 
                  src={game.url} 
                  title={game.name}
                  className="w-full h-full border-0" 
                  allowFullScreen
                ></iframe>
              </div>
            </div>
            
            <div className="bg-dark-light rounded-lg p-6">
              <h2 className="text-xl font-semibold text-white mb-4">About {game.name}</h2>
              <p className="text-gray-300 mb-4">{game.description}</p>
              <p className="text-gray-300">
                Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam in dui mauris. 
                Vivamus hendrerit arcu sed erat molestie vehicula. Sed auctor neque eu tellus rhoncus 
                ut eleifend nibh porttitor. Ut in nulla enim. Phasellus molestie magna non est 
                bibendum non venenatis nisl tempor.
              </p>
            </div>
          </div>
          
          {/* Related games */}
          <div className="lg:col-span-4">
            <div className="bg-dark-light rounded-lg p-6">
              <h3 className="text-xl font-semibold text-white mb-4">Similar Games</h3>
              <div className="space-y-4">
                {relatedGames.length > 0 ? (
                  relatedGames.map(relatedGame => (
                    <Link 
                      key={relatedGame.id} 
                      to={`/game/${relatedGame.id}`}
                      className="block group"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-16 h-16 bg-dark rounded overflow-hidden">
                          <img 
                            src={process.env.PUBLIC_URL + relatedGame.thumbnail} 
                            alt={relatedGame.name}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                          />
                        </div>
                        <div>
                          <h4 className="text-white group-hover:text-primary transition-colors">
                            {relatedGame.name}
                          </h4>
                          <p className="text-xs text-gray-400">{relatedGame.category}</p>
                        </div>
                      </div>
                    </Link>
                  ))
                ) : (
                  <p className="text-gray-400">No similar games found</p>
                )}
              </div>
            </div>
            
            <div className="bg-dark-light rounded-lg p-6 mt-6">
              <h3 className="text-xl font-semibold text-white mb-4">How to Play</h3>
              <ul className="text-gray-300 space-y-2 list-disc list-inside">
                <li>Use arrow keys or WASD to move</li>
                <li>Space bar to jump or activate special abilities</li>
                <li>Mouse click to interact with objects</li>
                <li>Press P to pause the game</li>
                <li>ESC to access the menu</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GamePage; 